import * as React from 'react';
import {
    Callout,
    DirectionalHint,
    classNamesFunction,
    IFocusZone,
    Announced,
    DelayedRender,
} from '@fluentui/react';
import {
    IGroupedComboBoxDropDownProps,
    IGroupedComboBoxDropDownStyleProps,
    IGroupedComboBoxDropDownStyles,
} from './GroupedComboBoxDropDown.types';
import { SectionedList } from '../SectionedList';
import {
    DropDownContext,
    hasNonEmptyGroup,
    GroupedComboBoxContext,
    filterGroupsBySubstringSearch,
    useDelayHoverOpenSubmenu,
} from 'grouped-combobox-core';
import { assert } from 'textfield-with-action';
import { useCalloutWidth } from './useCalloutWidth';
import preventDefaultCallback from '../utils/preventDefaultCallback';

const getClassNames = classNamesFunction<
    IGroupedComboBoxDropDownStyleProps,
    IGroupedComboBoxDropDownStyles
>();

const CALLOUT_BOTTOM_PADDING = 8;

export const GroupedComboBoxDropDownBase: React.FunctionComponent<IGroupedComboBoxDropDownProps> = (
    props: IGroupedComboBoxDropDownProps
) => {
    const {
        id,
        className,
        styles,
        theme,
        groups,
        scrollbarWidth,
        dropDownWidth,
        dropDownMaxWidth,
        dropDownMaxHeight,
        submenuMaxWidth,
        submenuMaxHeight,
        target,
        onAnnounceSearchResult,
        onDismiss,
        onItemClick,
        onItemSecondaryAction,
        onItemMouseEnter,
        onItemMouseLeave,
        onItemGotFocus,
        onItemLostFocus,
        filterText,
        renderEmptyContent,
        componentRef,
        onGroupToggled,
        iconResources,
        onRenderTooltip,
        selectedKey,
        onRenderMenu,
        directionalHintFixed = true,
        onRestoreFocus,
        getItemTooltip,
        groupStates = new Map<string, boolean>(),
        itemHeight,
    } = props;

    assert(theme);
    const classNames = getClassNames(styles, {
        theme: theme,
        className,
    });

    const [
        onItemMouseEnterBase,
        onItemMouseLeaveBase,
        onSubmenuOpen,
        onSubmenuClose,
        clearPending,
        onTouchStart,
    ] = useDelayHoverOpenSubmenu(onItemMouseEnter, onItemMouseLeave);

    React.useEffect(() => {
        return clearPending;
    }, [clearPending]);

    const dropDownContext = React.useMemo<GroupedComboBoxContext>(
        () => ({
            onItemClick,
            onItemSecondaryAction,
            onItemMouseEnter: onItemMouseEnterBase,
            onItemMouseLeave: onItemMouseLeaveBase,
            onItemGotFocus,
            onItemLostFocus,
            submenuMaxWidth,
            submenuMaxHeight,
            onSubmenuOpen,
            onSubmenuClose,
            iconResources,
            onRenderTooltip,
            onRenderMenu,
            onTouchStart,
            getItemTooltip,
        }),
        [
            onItemClick,
            onItemSecondaryAction,
            onItemMouseEnterBase,
            onItemMouseLeaveBase,
            onItemGotFocus,
            onItemLostFocus,
            submenuMaxWidth,
            submenuMaxHeight,
            onSubmenuOpen,
            onSubmenuClose,
            iconResources,
            onRenderTooltip,
            onRenderMenu,
            onTouchStart,
            getItemTooltip,
        ]
    );

    const filteredGroups = filterGroupsBySubstringSearch(groups, filterText);
    const searchHasNoResult = filteredGroups.length === 0 && hasNonEmptyGroup(groups);

    const [announceText, setAnnounceText] = React.useState<string | undefined>();

    const sectionListRef = React.useRef<IFocusZone>(null);
    React.useImperativeHandle(componentRef, () => ({
        announceText: setAnnounceText,
        focusDropDown: (): void => {
            sectionListRef.current?.focus();
        },
    }));

    const [calloutWidth, sectionedListElementRef] = useCalloutWidth(dropDownWidth, scrollbarWidth);

    const { separatorStyles, itemStyles, headerStyles } = classNames.subComponentStyles;

    // When filtering has changed, ask for a string to announce via screen reader
    const [filterResultAnnounce, setFilterResultAnnounce] = React.useState<string | undefined>();
    const nonEmptyGroup = hasNonEmptyGroup(groups);
    React.useEffect(() => {
        nonEmptyGroup &&
            setFilterResultAnnounce(
                filterText ? onAnnounceSearchResult?.(filteredGroups, filterText) : undefined
            );
    }, [filterText, filteredGroups, nonEmptyGroup, onAnnounceSearchResult]);

    const defaultRenderMenu = (): JSX.Element => (
        <SectionedList
            ref={sectionedListElementRef}
            componentRef={sectionListRef}
            groups={filteredGroups}
            separatorStyles={separatorStyles}
            sectionClassNames={classNames}
            itemStyles={itemStyles}
            onGroupToggled={onGroupToggled}
            selectedKey={selectedKey}
            onDismiss={onDismiss}
            width={dropDownWidth && scrollbarWidth ? dropDownWidth - scrollbarWidth : undefined}
            headerStyles={headerStyles}
            parentBottomPadding={CALLOUT_BOTTOM_PADDING}
            isFiltering={filterText !== undefined}
            groupStates={groupStates}
            itemHeight={itemHeight}
        />
    );

    return (
        <Callout
            id={id}
            role="menu"
            target={target}
            isBeakVisible={false}
            directionalHint={DirectionalHint.bottomLeftEdge}
            directionalHintForRTL={DirectionalHint.bottomRightEdge}
            directionalHintFixed={directionalHintFixed}
            alignTargetEdge={true}
            onDismiss={onDismiss}
            calloutMaxHeight={dropDownMaxHeight}
            calloutWidth={calloutWidth}
            calloutMaxWidth={dropDownMaxWidth}
            shouldRestoreFocus={true}
            onRestoreFocus={onRestoreFocus}
            minPagePadding={CALLOUT_BOTTOM_PADDING}
            // prevent mouse down on scrollbar to steal keyboard focus.
            onMouseDown={preventDefaultCallback}
        >
            {
                <DelayedRender>
                    <Announced aria-live="polite" message={announceText} />
                </DelayedRender>
            }
            {
                <DelayedRender>
                    <Announced aria-live="polite" message={filterResultAnnounce} />
                </DelayedRender>
            }
            {searchHasNoResult ? (
                renderEmptyContent?.(filterText)
            ) : (
                <DropDownContext.Provider value={dropDownContext}>
                    {onRenderMenu
                        ? onRenderMenu({ isSubmenu: false }, defaultRenderMenu)
                        : defaultRenderMenu()}
                </DropDownContext.Provider>
            )}
        </Callout>
    );
};
